export type ControlId = string;

export interface Layout {
  /** A section is a grouping of controls in a particular layout. Sections are rendered hoziontally, with a vertical divider between them.
   *
   *
   *     (SingleRowSection)                   (MultiRowSection)
   * --------------                   d
   * |            |   Small           i   Small Small Small Small
   * | Big Button |   Small           v   Small Small Small Small
   * |            |   Small           i   Small Small Small Small
   * |            |                   d
   * --------------                   e
   *                                  r
   */
  sections: Array<Section | LayoutDivider>;
}

type AdditionalAriaProps = {
  id: string;
  labelledBy: string;
};

export type Section = SingleRowSection | MultiRowSection | ScopedIndexLayout;
export type SectionItem = SingleRowSectionItem | MultiRowSectionItem;

export type SingleRowSection = [
  | SingleRowSectionItem[]
  | ScopedIndexLayout[]
  | (SingleRowSectionItem | ScopedIndexLayout)[],
];
/** A row can contain a control, a vertical stack of 3 controls, or a divider */
export type SingleRowSectionItem =
  | ControlId
  | VerticalControlStackWithForProps
  | LayoutDivider;
/**
 * Up to 3 controls may be stacked vertically.
 * If null is provided for the final control Id, a space will remain for the control.
 * Otherwise the controls will be distributed evenly vertically
 */
export type VerticalControlStack =
  | [ControlId]
  | [ControlId, ControlId | null]
  | [ControlId, ControlId, ControlId | null]
  | [ControlId, null, null];

export type VerticalControlStackWithForProps = VerticalControlStack & {
  ariaProps?: AdditionalAriaProps;
};

export type MultiRowSection =
  | [
      MultiRowSectionItem[] | ScopedIndexLayout,
      MultiRowSectionItem[] | ScopedIndexLayout,
    ]
  | [
      MultiRowSectionItem[] | ScopedIndexLayout,
      MultiRowSectionItem[] | ScopedIndexLayout,
      MultiRowSectionItem[] | ScopedIndexLayout,
    ];
export type MultiRowSectionItem = ControlId | LayoutDivider | ScopedIndexLayout;

export enum MLRKeytipLocation {
  Top = 0,
  Middle = 1,
  Bottom = 2,
}

type LayoutIndex = { layoutIndex: number };

export type ScopedIndexLayout = LayoutIndex & {
  controlId:
    | (
        | ControlId
        | LayoutDivider
        | MultiRowSectionItem
        | SingleRowSectionItem
        | SingleRowSection
      )[]
    | SingleRowSection;
};

/** A vertical divider between row items */
export interface LayoutDivider {
  type: "Divider";
}

export const dataLid = "data-lid";
export const initialDataLid = 0;
export const groupFlyoutDataLid = -1;

export function isMultiRowSection(section: Section | LayoutDivider) {
  return Array.isArray(section) && section.length > 1;
}

export function isMultiControlRow(
  row:
    | (ScopedIndexLayout | SingleRowSectionItem)[]
    | MultiRowSectionItem[]
    | ScopedIndexLayout
) {
  return Array.isArray(row) && row.length > 1;
}

export function sections(...sections: Array<Section | LayoutDivider>): Layout {
  return { sections };
}

export function singleRow(
  ...controls: (SingleRowSectionItem | ScopedIndexLayout)[]
): SingleRowSection {
  return [controls];
}

export function multiRow(
  firstRow: MultiRowSectionItem[] | ScopedIndexLayout,
  secondRow: MultiRowSectionItem[] | ScopedIndexLayout,
  thirdRow?: MultiRowSectionItem[] | ScopedIndexLayout
): MultiRowSection {
  return !thirdRow ? [firstRow, secondRow] : [firstRow, secondRow, thirdRow];
}

export function divider(): LayoutDivider {
  return { type: "Divider" };
}

export function row(
  ...controlId: Array<ControlId | LayoutDivider | ScopedIndexLayout>
): MultiRowSectionItem[] {
  return [...controlId];
}

export function scopedLayout(
  layoutIndex: number,
  ...controlId: any[]
): ScopedIndexLayout {
  // If a row is directly inside of the scopedLayout, it will have already converted its parameters to an array
  // so we need to re-flatten them
  const flatControlIds =
    controlId.length === 1 && Array.isArray(controlId[0])
      ? (controlId[0] as SectionItem[])
      : controlId;

  return {
    layoutIndex,
    controlId: flatControlIds,
  };
}

/**
 * @param bottom if provided, the stack will have space at the bottom for a control. Otherwise, the stack will be spaced evenly vertically
 */
export function stack(top: string): VerticalControlStack;
export function stack(top: string, bottom: string | null): VerticalControlStack;
export function stack(
  top: string,
  middle: string,
  bottom: string | null
): VerticalControlStack;
export function stack(
  top: string,
  middle: null,
  bottom: null
): VerticalControlStack;
export function stack(
  top: string,
  middle: string,
  bottom?: string | null
): VerticalControlStack;
export function stack(
  top: string,
  middle?: string | null,
  bottom?: string | null
): VerticalControlStack {
  if (middle === undefined) return [top];

  if (bottom === undefined) return [top, middle];

  return [top, middle!, bottom];
}

/**
 * This function provides a way for stack to have an associated Id and aria-labelledBy attribute
 * to allow for controls such as an AppLabel to properly identify the group to accessibility tools
 */
export function stackWithAdditionalAriaProps(
  ariaProps: AdditionalAriaProps,
  top: string,
  middle: string,
  bottom?: string | null
): VerticalControlStackWithForProps {
  const verticalControlStack: VerticalControlStackWithForProps = stack(
    top,
    middle,
    bottom
  );
  verticalControlStack.ariaProps = ariaProps;
  return verticalControlStack;
}

export function getDataLidJsxAttribute(layoutIndex: number) {
  return {
    [dataLid]: layoutIndex,
  };
}

export function getDataLidSelector(layoutIndex?: number) {
  return `[${dataLid}${layoutIndex !== undefined ? `="${layoutIndex}"` : ""}]`;
}
